import React from 'react';
import { Button, Icon } from '@saviynt/design-system';
import cx from 'classnames';

import { testIds } from '../../constants';
import { type ActionBlockOption } from '../../types';
import Prompt from '../Prompt';

import './Actions.css';

export const ACTION_OPTION_TYPE = {
  BUTTON: 'button',
  SUGGESTION: 'suggestion',
};

export const ACTION_OPTION_VARIANT = {
  FILLED: 'filled',
  OUTLINED: 'outlined',
  CHIP: 'chip',
};

interface ActionsProps {
  id: string;
  options: ActionBlockOption[];
  onSubmit?: (option: ActionBlockOption) => void;
  isDisabled?: boolean;
}

/**
 * Actions
 */
function Actions({ id, options, onSubmit, isDisabled }: ActionsProps) {
  const handleSubmit = (option: ActionBlockOption) => onSubmit?.(option);

  const getButton = (option: ActionBlockOption, key: string) => {
    if (option.variant === ACTION_OPTION_VARIANT.FILLED) {
      return (
        <Button
          key={key}
          kind='filled'
          onClick={() => handleSubmit(option)}
          isDisabled={Boolean(isDisabled)}>
          {option.label}
        </Button>
      );
    }

    if (option.variant === ACTION_OPTION_VARIANT.OUTLINED) {
      return (
        <Button
          key={key}
          kind='ghost'
          onClick={() => handleSubmit(option)}
          isDisabled={Boolean(isDisabled)}>
          {option.label}
        </Button>
      );
    }

    return (
      <Prompt
        key={key}
        kind='savi'
        size='small'
        textContent={option.label}
        onClick={() => handleSubmit(option)}
        isDisabled={Boolean(isDisabled)}
      />
    );
  };

  if (options.length === 0) {
    return null;
  }

  return (
    <div
      data-scroll-id={id}
      className={cx('Actions')}
      data-testid={testIds.GEN_ACTION_ID}>
      {options.map((option) => {
        const { label, type } = option;
        const key = `${type}-${label}`;

        if (type === ACTION_OPTION_TYPE.BUTTON) {
          return getButton(option, key);
        }

        return (
          <Prompt
            key={key}
            kind='savi'
            size='small'
            leftIcon={<Icon kind='Comment' />}
            textContent={label}
            onClick={() => handleSubmit(option)}
          />
        );
      })}
    </div>
  );
}

Actions.defaultProps = {
  onSubmit: undefined,
  isDisabled: false,
};

export default Actions;
